import React, { useEffect, useState } from "react";
import { 
  Video, 
  Plus, 
  Trash2, 
  Calendar, 
  Users, 
  Building2,
  Play,
  Link,
  Clock
} from "lucide-react";
import adminService from "../../../../services/adminService";
import "./Admintraining.scss";

function extractYoutubeId(url) {
  const match = url.match(/(?:youtu\.be\/|v=)([^&]+)/);
  return match ? match[1] : null;
}

const AUDIENCE_LABELS = {
  freelancer: "Freelancers",
  company: "Empresas",
};

const AUDIENCE_ICONS = {
  freelancer: Users,
  company: Building2,
};

const TABS = [
  { key: "todos", label: "Todos" },
  { key: "freelancer", label: "Freelancers" },
  { key: "company", label: "Empresas" },
];

export default function AdminTraining() {
  const [videos, setVideos] = useState([]);
  const [tab, setTab] = useState("todos");
  const [form, setForm] = useState({ 
    title: "", 
    youtubeUrl: "", 
    description: "", 
    audience: "freelancer" 
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminService.getTrainingVideos();
      setVideos(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handlePublish = async () => {
    if (!form.title.trim() || !form.youtubeUrl.trim()) {
      alert("Preencha todos os campos obrigatórios.");
      return;
    }

    if (!extractYoutubeId(form.youtubeUrl)) {
      alert("URL do YouTube inválida. Use um link como https://youtube.com/watch?v=... ou https://youtu.be/...");
      return;
    }

    setSaving(true);
    try {
      await adminService.createTrainingVideo(form);
      setForm({ title: "", youtubeUrl: "", description: "", audience: "freelancer" });
      await load();
      alert("Vídeo publicado com sucesso!");
    } catch (error) {
      alert("Erro ao publicar vídeo. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Tem certeza que deseja remover este vídeo?")) return;
    try {
      await adminService.deleteTrainingVideo(id);
      await load();
    } catch (error) {
      alert("Erro ao remover vídeo.");
    }
  };

  const filteredVideos = tab === "todos" ? videos : videos.filter((v) => v.audience === tab);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("pt-BR", { 
      day: "2-digit", 
      month: "short", 
      year: "numeric" 
    });
  };

  return (
    <div className="admin-training">
      {/* Header */}
      <div className="admin-training__header">
        <div>
          <div className="admin-training__title">
            <Video size={22} />
            <h2>Vídeos de Treinamento</h2>
          </div>
          <p className="admin-training__subtitle">
            Gerencie os vídeos educativos disponíveis para freelancers e empresas
          </p>
        </div>
        <div className="admin-training__count">
          <span>{filteredVideos.length} vídeos</span>
        </div>
      </div>

      {/* Formulário */}
      <div className="admin-training-form">
        <div className="admin-training-form__title">
          <Plus size={18} />
          Adicionar Novo Vídeo
        </div>

        <div className="admin-training-form__grid">
          <div className="admin-training-form__field">
            <label>
              Público <span className="required">*</span>
            </label>
            <select
              value={form.audience}
              onChange={(e) => setForm({ ...form, audience: e.target.value })}
            >
              <option value="freelancer">Freelancers</option>
              <option value="company">Empresas</option>
            </select>
          </div>

          <div className="admin-training-form__field">
            <label>
              Título <span className="required">*</span>
            </label>
            <input
              placeholder="Ex: Como se destacar nas vagas"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </div>

          <div className="admin-training-form__field">
            <label>
              URL do YouTube <span className="required">*</span>
            </label>
            <input
              placeholder="https://youtube.com/watch?v=..."
              value={form.youtubeUrl}
              onChange={(e) => setForm({ ...form, youtubeUrl: e.target.value })}
            />
          </div>
        </div>

        <div className="admin-training-form__description">
          <label>Descrição (opcional)</label>
          <input
            placeholder="Breve descrição sobre o conteúdo do vídeo..."
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>

        <div className="admin-training-form__actions">
          <button 
            className="admin-training-form__btn admin-training-form__btn--primary" 
            disabled={saving} 
            onClick={handlePublish}
          >
            {saving ? (
              "Publicando..."
            ) : (
              <>
                <Plus size={16} />
                Publicar Vídeo
              </>
            )}
          </button>
          <span className="admin-training-form__hint">
            <Link size={14} />
            Vídeos são exibidos na aba "Treinamento" do usuário
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-training-tabs">
        {TABS.map((t) => (
          <button 
            key={t.key} 
            className={tab === t.key ? "active" : ""} 
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Lista de vídeos */}
      {loading ? (
        <div className="admin-training-empty">
          <Play size={40} />
          <h3>Carregando vídeos...</h3>
        </div>
      ) : filteredVideos.length > 0 ? (
        <div className="admin-training-list">
          {filteredVideos.map((v) => {
            const ytId = extractYoutubeId(v.youtubeUrl);
            const AudienceIcon = AUDIENCE_ICONS[v.audience] || Users;
            const audienceLabel = AUDIENCE_LABELS[v.audience] || v.audience;

            return (
              <div className="admin-training-item" key={v.id}>
                {ytId ? (
                  <img
                    className="admin-training-item__thumbnail"
                    src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`}
                    alt={v.title}
                    loading="lazy"
                  />
                ) : (
                  <div className="admin-training-item__placeholder">
                    <Play size={24} />
                  </div>
                )}

                <div className="admin-training-item__info">
                  <div className="admin-training-item__top">
                    <h4 className="admin-training-item__title">{v.title}</h4>
                    <span className={`admin-training-item__badge admin-training-item__badge--${v.audience}`}>
                      <AudienceIcon size={12} />
                      {audienceLabel}
                    </span>
                  </div>

                  {v.description && (
                    <p className="admin-training-item__description">{v.description}</p>
                  )}

                  <div className="admin-training-item__meta">
                    <span>
                      <Calendar size={14} />
                      {formatDate(v.publishedAt)}
                    </span>
                    {ytId && (
                      <span>
                        <Link size={14} />
                        <a 
                          href={`https://youtube.com/watch?v=${ytId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: 'var(--training-primary)', textDecoration: 'none' }}
                        >
                          Assistir
                        </a>
                      </span>
                    )}
                  </div>
                </div>

                <div className="admin-training-item__actions">
                  <button 
                    className="admin-training-item__btn admin-training-item__btn--danger" 
                    onClick={() => handleDelete(v.id)}
                  >
                    <Trash2 size={14} />
                    Remover
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="admin-training-empty">
          <Video size={40} />
          <h3>Nenhum vídeo encontrado</h3>
          <p>
            {tab === "todos" 
              ? "Comece publicando seu primeiro vídeo de treinamento." 
              : `Nenhum vídeo cadastrado para ${AUDIENCE_LABELS[tab].toLowerCase()}.`}
          </p>
        </div>
      )}
    </div>
  );
}